import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Wand2, Sparkles, TrendingUp, TrendingDown, Minus, ArrowRight, Loader2,
  Info, RefreshCw, CheckCircle2, Trophy, Users, CalendarDays, X, Zap
} from "lucide-react";
import api from "@/lib/api";
import { frenchDate } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

const LEVELS = {
  Fort: { color: "var(--affinity-strong)", bg: "rgba(31,157,85,0.12)", border: "rgba(31,157,85,0.28)", text: "#146B3A", Icon: TrendingUp },
  Moyen: { color: "var(--affinity-medium)", bg: "rgba(47,111,237,0.10)", border: "rgba(47,111,237,0.24)", text: "#1F4FB0", Icon: Minus },
  Faible: { color: "var(--affinity-weak)", bg: "rgba(192,57,43,0.10)", border: "rgba(192,57,43,0.24)", text: "#8F2A20", Icon: TrendingDown },
};

/** Badge d'affinité Fort / Moyen / Faible (texte explicite + couleur). */
const AffinityBadge = ({ level, testid }) => {
  const l = LEVELS[level] || LEVELS.Moyen;
  const { Icon } = l;
  return (
    <span data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold"
      style={{ background: l.bg, borderColor: l.border, color: l.text }}>
      <Icon className="h-3 w-3" /> {level}
    </span>
  );
};

/** Compteur animé (requestAnimationFrame) — transform/opacity only. */
function useCountUp(target, active, duration = 900) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!active) { setVal(0); return; }
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (target - from) * eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, active, duration]);
  return val;
}

const cardCls = "rounded-[var(--radius)] border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";

export function OptimizeAttributionSheet({ open, onOpenChange, week, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [assocId, setAssocId] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | running | ready | applied
  const [ignored, setIgnored] = useState({}); // booking_id -> true
  const [applying, setApplying] = useState(false);
  const [runPct, setRunPct] = useState(0);

  const weekIso = useMemo(() => {
    if (!week) return undefined;
    if (typeof week === "string") return week;
    try { return week.toISOString().slice(0, 10); } catch { return undefined; }
  }, [week]);

  const fetchData = useCallback(async (aid) => {
    setLoading(true); setError(null);
    try {
      const params = { week: weekIso };
      if (aid) params.association_id = aid;
      const { data: d } = await api.get("/planning/optimize", { params });
      setData(d);
      setAssocId(d.association_id || (d.associations?.[0]?.id ?? null));
      setPhase("idle");
      setIgnored({});
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de charger l'optimisation.");
    } finally { setLoading(false); }
  }, [weekIso]);

  useEffect(() => {
    if (open) fetchData(null);
    else { setPhase("idle"); setData(null); setError(null); setRunPct(0); }
  }, [open, fetchData]);

  const onSelectAssoc = (aid) => { setAssocId(aid); fetchData(aid); };

  // Animation "live" de la moulinette puis révélation du résultat
  const runOptimization = () => {
    if (!data?.optimizable) return;
    setPhase("running"); setRunPct(0);
    const start = performance.now();
    const dur = 1500;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setRunPct(Math.round(t * 100));
      if (t < 1) requestAnimationFrame(step);
      else setPhase("ready");
    };
    requestAnimationFrame(step);
  };

  const activeChanges = useMemo(
    () => (data?.changes || []).filter((c) => !ignored[c.booking_id]),
    [data, ignored]);

  // Score "après" recalculé selon les changements NON ignorés (gain cumulé)
  const effectiveAfter = useMemo(() => {
    if (!data) return 0;
    const ignoredGain = (data.changes || [])
      .filter((c) => ignored[c.booking_id])
      .reduce((s, c) => s + (c.gain || 0), 0);
    const slotCount = data.slot_count || 1;
    return Math.max(0, +(data.score_after - ignoredGain / slotCount).toFixed(1));
  }, [data, ignored]);

  const showResult = phase === "ready" || phase === "applied";
  const animatedAfter = useCountUp(effectiveAfter, showResult);

  const apply = async () => {
    if (activeChanges.length === 0) return;
    setApplying(true);
    try {
      const changes = activeChanges.map((c) => ({ booking_id: c.booking_id, ligne_id: c.to_line.id }));
      const { data: res } = await api.post("/planning/optimize/apply", { changes });
      toast.success("Attribution optimisée appliquée", {
        description: `${res.applied_count} réattribution(s) appliquée(s).`,
      });
      setPhase("applied");
      onApplied?.(activeChanges.map((c) => c.booking_id));
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'application.");
    } finally { setApplying(false); }
  };

  const toggleIgnore = (bid) =>
    setIgnored((prev) => ({ ...prev, [bid]: !prev[bid] }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="optimization-sheet"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] xl:max-w-[720px]">
        {/* Header */}
        <SheetHeader className="shariti-gradient-header border-b border-[var(--border-soft)] px-5 py-4 text-left">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/70 text-[var(--shariti-orange)] shadow-[var(--shadow-sm)]">
              <Wand2 className="h-5 w-5" />
            </span>
            <div>
              <SheetTitle className="font-display text-lg font-bold tracking-tight">Optimisation de l'attribution</SheetTitle>
              <SheetDescription className="text-xs">
                Permutation des lignes sur les spots déjà planifiés — {data?.association_acronym ? `${data.association_acronym} · ` : ""}Semaine {data?.week_number ?? ""}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Contrôles */}
          <div className={`${cardCls} mb-4 p-3`} data-testid="optimization-controls">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[220px] flex-1">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Association</label>
                <Select value={assocId || undefined} onValueChange={onSelectAssoc} disabled={loading || !data?.associations?.length}>
                  <SelectTrigger className="h-9" data-testid="optimization-association-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {(data?.associations || []).map((a) => (
                      <SelectItem key={a.id} value={a.id} data-testid={`optimization-assoc-opt-${a.acronym}`}>
                        {a.acronym} — {a.slot_count} spot(s) · {a.line_count} ligne(s)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-1.5 text-center">
                <div className="text-[10px] font-semibold uppercase text-muted-ink">Semaine</div>
                <div className="text-sm font-bold" data-testid="optimization-week">S{data?.week_number ?? "—"}</div>
              </div>
              <Button onClick={runOptimization}
                disabled={loading || phase === "running" || !data?.optimizable}
                className="h-9 bg-[var(--shariti-orange)] text-white transition-transform duration-150 hover:-translate-y-px hover:brightness-[0.98] active:scale-[0.98]"
                data-testid="optimization-run-button">
                {phase === "running" ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Calcul…</>
                  : <><Zap className="mr-1.5 h-4 w-4" />Lancer l'optimisation</>}
              </Button>
            </div>
          </div>

          {/* États */}
          {loading && (
            <div className="space-y-3" data-testid="optimization-loading-state">
              <Skeleton className="h-24 w-full rounded-[var(--radius)]" />
              <Skeleton className="h-40 w-full rounded-[var(--radius)]" />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-[var(--radius)] border border-[rgba(192,57,43,0.35)] bg-[rgba(192,57,43,0.06)] p-4 text-sm text-[#8F2A20]" data-testid="optimization-error-state">
              <div className="mb-2 font-semibold">{error}</div>
              <Button variant="secondary" size="sm" onClick={() => fetchData(assocId)} className="border border-[var(--border-soft)] bg-white">
                <RefreshCw className="mr-1.5 h-4 w-4" />Réessayer
              </Button>
            </div>
          )}

          {!loading && !error && data && !data.optimizable && (
            <div className="flex items-start gap-3 rounded-[var(--radius)] border border-[rgba(47,111,237,0.28)] bg-[rgba(47,111,237,0.06)] p-4 text-sm" data-testid="optimization-empty-state">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#1F4FB0]" />
              <div>
                <div className="font-semibold text-[#1F4FB0]">Optimisation impossible pour cette sélection</div>
                <div className="mt-1 text-muted-ink">{data.reason}</div>
              </div>
            </div>
          )}

          {/* Run live */}
          {!loading && !error && data?.optimizable && phase === "running" && (
            <div className={`${cardCls} p-5`} data-testid="optimization-live-run">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4 text-[var(--shariti-orange)]" />
                Analyse des affinités terrain…
              </div>
              <Progress value={runPct} className="h-2" data-testid="optimization-run-progress" />
              <div className="mt-3 space-y-1 text-xs text-muted-ink">
                <div>• Reconstitution de l'historique des passages par équipe</div>
                {runPct > 35 && <div className="reveal-item">• Croisement enseigne × secteur × potentiel de flux</div>}
                {runPct > 50 && data?.performance_active && (
                  <div className="reveal-item">• Rendement réel des équipes (bulletins collectés par ville)</div>
                )}
                {runPct > 70 && <div className="reveal-item">• Recherche de la meilleure répartition par jour</div>}
              </div>
            </div>
          )}

          {/* Résultat */}
          {!loading && !error && data?.optimizable && showResult && (
            <div className="space-y-4">
              {/* Score panel */}
              <div className={`${cardCls} p-4`} data-testid="optimization-score-panel">
                <div className="grid grid-cols-3 gap-3">
                  <ScoreCard label="Affinité avant" value={data.score_before} testid="optimization-score-before" muted />
                  <ScoreCard label="Affinité après" value={animatedAfter} testid="optimization-score-after" highlight />
                  <ScoreCard label="Gain" value={+(effectiveAfter - data.score_before).toFixed(1)} testid="optimization-score-gain" gain />
                </div>
                <div className="mt-3">
                  <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-muted-ink">
                    <span>Avant</span><span>Après</span>
                  </div>
                  <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
                    <div className="absolute inset-y-0 left-0 rounded-full bg-[rgba(24,24,24,0.18)] transition-[width] duration-500 ease-out" style={{ width: `${data.score_before}%` }} />
                    <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--shariti-orange)] transition-[width] duration-700 ease-out" style={{ width: `${showResult ? effectiveAfter : data.score_before}%` }} />
                  </div>
                  <div className="mt-2 text-center text-xs text-muted-ink">
                    {activeChanges.length === 0
                      ? "Répartition déjà optimale — aucun changement nécessaire."
                      : <><span className="font-bold text-ink">{activeChanges.length}</span> réattribution(s) proposée(s) sur {data.slot_count} journée(s)-spot</>}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="changes">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="changes" data-testid="optimization-changes-tab">Changements ({data.change_count})</TabsTrigger>
                  <TabsTrigger value="lineup" data-testid="optimization-lineup-tab">Composition par ligne</TabsTrigger>
                </TabsList>

                <TabsContent value="changes" className="mt-3">
                  {(data.changes || []).length === 0 ? (
                    <div className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border-soft)] bg-[var(--surface-2)] p-4 text-sm text-muted-ink">
                      <CheckCircle2 className="h-5 w-5 text-[var(--affinity-strong)]" /> Aucun changement : chaque équipe est déjà sur son meilleur spot.
                    </div>
                  ) : (
                    <TooltipProvider delayDuration={150}>
                      <ScrollArea className="max-h-[46vh]">
                        <div className="space-y-2 pr-2" data-testid="optimization-changes-list">
                          {data.changes.map((c, i) => {
                            const off = !!ignored[c.booking_id];
                            return (
                              <div key={c.booking_id}
                                data-testid="optimization-change-row"
                                className={`reveal-item ${cardCls} flex items-center gap-3 p-3 transition-[box-shadow,border-color,opacity] duration-200 ease-out hover:border-[rgba(238,114,46,0.3)] hover:shadow-[var(--shadow-sm)] ${off ? "opacity-45" : ""}`}
                                style={{ animationDelay: `${Math.min(i, 12) * 55}ms` }}>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-ink">
                                    <CalendarDays className="h-3 w-3" />{c.day_label} {frenchDate(c.date)}
                                  </div>
                                  <div className="truncate text-sm font-bold" title={c.spot.name}>{c.spot.name}</div>
                                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px]">
                                    <span className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 text-muted-ink line-through">{c.from_line.label}</span>
                                    <ArrowRight className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                                    <span className="rounded-md bg-[rgba(238,114,46,0.1)] px-1.5 py-0.5 font-semibold text-[#8A3A10]">{c.to_line.label}</span>
                                  </div>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="mt-1 line-clamp-1 cursor-help text-[11px] text-muted-ink">{c.justification}</div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[260px] text-xs">{c.justification}</TooltipContent>
                                  </Tooltip>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1.5">
                                  <AffinityBadge level={c.level} testid={`optimization-change-level-${c.booking_id}`} />
                                  {c.gain > 0 && <span className="text-[11px] font-bold text-[var(--affinity-strong)]">+{c.gain} pts</span>}
                                  <label className="flex cursor-pointer items-center gap-1 text-[10px] font-semibold text-muted-ink">
                                    <Checkbox checked={off} onCheckedChange={() => toggleIgnore(c.booking_id)}
                                      data-testid={`optimization-change-ignore-checkbox-${c.booking_id}`} className="h-3.5 w-3.5" />
                                    Ignorer
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </TooltipProvider>
                  )}
                </TabsContent>

                <TabsContent value="lineup" className="mt-3">
                  <ScrollArea className="max-h-[46vh]">
                    <div className="grid grid-cols-1 gap-3 pr-2 md:grid-cols-2" data-testid="optimization-lineup">
                      {(data.lineup || []).map((lu) => (
                        <div key={lu.line.id} className={`${cardCls} p-3`} data-testid="optimization-lineup-card">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: lu.line.color }} />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-bold" title={lu.line.label}>{lu.line.label}</div>
                              <div className="text-[11px] text-muted-ink">{lu.slots.length} spot(s) · {lu.line.cdp_name || "—"}</div>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-1.5">
                            {["Fort", "Moyen", "Faible"].map((lv) => lu.counts[lv] > 0 && (
                              <span key={lv} className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                                style={{ background: LEVELS[lv].bg, color: LEVELS[lv].text }}>
                                {lu.counts[lv]} {lv}
                              </span>
                            ))}
                          </div>
                          <div className="mt-2 space-y-1">
                            {lu.slots.map((s) => (
                              <div key={s.booking_id} className="flex items-center justify-between gap-2 rounded-md bg-[var(--surface-2)] px-2 py-1 text-[11px]">
                                <span className="truncate" title={s.spot.name}>
                                  <span className="font-semibold text-muted-ink">{s.day_label.slice(0, 3)}.</span> {s.spot.name}
                                </span>
                                <span className="shrink-0 font-bold" style={{ color: LEVELS[s.level].color }}>{s.score}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>

              <div className="flex items-start gap-2 rounded-[var(--radius)] border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] text-muted-ink">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {data?.performance_active
                  ? "Score basé sur l'historique terrain (passages, familiarité enseigne/secteur, potentiel de flux) ET la performance réelle : bulletins collectés par l'équipe dans la ville. Les dates et les spots ne changent pas."
                  : "Score d'affinité basé sur l'historique terrain (passages de l'équipe/CDP sur le spot, familiarité enseigne/secteur, potentiel de flux). Les dates et les spots ne changent pas."}
              </div>
            </div>
          )}

          {/* Avant de lancer : petit récap incitatif */}
          {!loading && !error && data?.optimizable && phase === "idle" && (
            <div className={`${cardCls} flex items-center gap-3 p-4`}>
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--draft-glow)] text-[var(--shariti-orange)]"><Trophy className="h-5 w-5" /></span>
              <div className="text-sm">
                <div className="font-semibold">{data.slot_count} journée(s)-spot · {data.lines?.length} ligne(s)</div>
                <div className="text-muted-ink">Lancez la moulinette pour trouver la meilleure équipe sur chaque spot.</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-[var(--border-soft)] bg-white/90 px-5 py-3 backdrop-blur-sm">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="optimization-cancel-button"
            className="border border-[var(--border-soft)]">
            <X className="mr-1.5 h-4 w-4" />Fermer
          </Button>
          <Button onClick={apply}
            disabled={!showResult || applying || activeChanges.length === 0}
            className="bg-[var(--shariti-orange)] text-white transition-transform duration-150 hover:-translate-y-px hover:brightness-[0.98] active:scale-[0.98]"
            data-testid="optimization-apply-button">
            {applying ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Application…</>
              : <><CheckCircle2 className="mr-1.5 h-4 w-4" />Appliquer{activeChanges.length ? ` (${activeChanges.length})` : ""}</>}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const ScoreCard = ({ label, value, testid, muted, highlight, gain }) => {
  const display = gain ? `${value > 0 ? "+" : ""}${(value).toFixed(1)}` : (Math.round(value * 10) / 10).toFixed(1);
  const color = highlight ? "var(--shariti-orange)" : gain ? "var(--affinity-strong)" : "var(--shariti-ink)";
  return (
    <div className={`rounded-[var(--radius)] border border-[var(--border-soft)] p-3 text-center ${highlight ? "bg-[rgba(238,114,46,0.06)]" : "bg-white"}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-ink">{label}</div>
      <div className="mt-0.5 text-2xl font-bold tabular-nums md:text-3xl" style={{ color: muted ? "var(--ink-muted, rgba(24,24,24,0.55))" : color }} data-testid={testid}>
        {display}
      </div>
      <div className="text-[9px] uppercase text-muted-ink">/ 100</div>
    </div>
  );
};
